'use client';

import { useEffect, useRef, type RefObject } from 'react';
import type { ReportInsight } from '@/lib/report/answer-evidence';
import { reportPriceLabel } from '@/lib/report-price';
import ReportOpeningBrief from './ReportOpeningBrief';

export default function ReportPreviewDialog({
  open, onClose, insight, pending, ideologyName, previewHref, onBuy, buying, error, returnFocusRef,
}: {
  open: boolean;
  onClose: () => void;
  insight: ReportInsight | null;
  pending: boolean;
  ideologyName: string;
  previewHref: string;
  onBuy: () => void;
  buying: boolean;
  error: string | null;
  returnFocusRef: RefObject<HTMLButtonElement | null>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousFocus = document.activeElement;
    const opener = returnFocusRef.current;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus({ preventScroll: true });
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      const target = opener?.isConnected ? opener : previousFocus;
      if (target instanceof HTMLElement && target.isConnected) target.focus({ preventScroll: true });
    };
  }, [open, returnFocusRef]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="report-reader-title"
      onCancel={event => { event.preventDefault(); onClose(); }}
      onKeyDown={event => {
        if (event.key !== 'Tab') return;
        const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], [tabindex="0"]'))
          .filter(el => el.getClientRects().length > 0);
        if (!controls.length) return;
        event.preventDefault();
        const current = controls.indexOf(document.activeElement as HTMLElement);
        const next = event.shiftKey
          ? (current <= 0 ? controls.length - 1 : current - 1)
          : (current + 1) % controls.length;
        controls[next].focus();
      }}
      className="fixed inset-0 m-auto hidden open:flex flex-col h-[100dvh] max-h-[100dvh] w-full max-w-none sm:h-[min(820px,calc(100dvh-48px))] sm:max-w-[720px] sm:rounded-2xl border-0 p-0 bg-white text-[#16223d] shadow-2xl backdrop:bg-[#16223d]/45"
    >
      <header className="shrink-0 flex items-start justify-between gap-4 border-b border-[#16223d]/15 px-5 py-4 sm:px-8">
        <div className="min-w-0">
          <h2 id="report-reader-title" className="text-sm font-semibold leading-relaxed">Votely / Personal Political Profile</h2>
          <p className="text-xs text-[#52658a] mt-1 break-words">{insight ? 'Your report excerpt' : 'Report preview'} · {ideologyName}</p>
        </div>
        <button ref={closeRef} type="button" aria-label="Close report preview" onClick={onClose} className="shrink-0 grid place-items-center size-11 -my-1 rounded-full border border-[#16223d]/15 hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2a4173]">
          <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </button>
      </header>
      <div data-testid="report-reader-scroll" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-8 sm:p-10">
        {pending ? <p role="status" className="text-base text-[#52658a]">Preparing your excerpt…</p> : insight ? (
          <>
            <p className="text-xs font-semibold tracking-widest text-[#52658a] mb-5">A PATTERN IN YOUR ANSWERS</p>
            <ReportOpeningBrief insight={insight} />
          </>
        ) : (
          <article>
            <h3 className="font-display text-3xl leading-tight mb-5">Inside the full report</h3>
            <p className="text-base leading-relaxed mb-5">A personal excerpt isn&apos;t available for this saved result, so the overview below explains what the full report covers.</p>
            <div className="space-y-5 text-base leading-relaxed">
              <p>The report explains how your answers contribute to the three main scores, with personal analysis where your saved answers support it.</p>
              <p>It also covers the ideas, disagreements and strongest criticisms within your political tradition, with researched background shared by readers who get the same result.</p>
              <p>The reading list helps you explore these ideas further and find out more about the political tradition behind your result.</p>
            </div>
          </article>
        )}
        <div className="mt-8 pt-6 border-t border-[#16223d]/15">
          {insight && <p className="text-sm leading-relaxed text-[#52658a] mb-4">Your full report includes this passage alongside further analysis of your answers and background on your political tradition.</p>}
          <a href={previewHref} className="text-sm font-semibold text-[#2a4173] underline underline-offset-4">Open the full-page preview</a>
        </div>
      </div>
      <footer className="shrink-0 border-t border-[#16223d]/15 bg-white px-5 pt-4 pb-[max(16px,env(safe-area-inset-bottom))] sm:px-8">
        <div className="flex flex-wrap justify-between items-baseline gap-x-3 gap-y-1 mb-3">
          <p className="font-display text-lg font-semibold">Your full report</p>
          <p className="text-xs text-[#52658a]">A single payment of {reportPriceLabel()} gives you access to the full report.</p>
        </div>
        <button type="button" onClick={onBuy} disabled={buying} className="w-full rounded-xl bg-[#2a4173] px-4 py-3.5 text-[15px] leading-snug font-semibold text-white hover:bg-[#1f3258] disabled:opacity-60">
          {buying ? 'Opening secure checkout…' : `Get your full profile, ${reportPriceLabel()}`}
        </button>
        {error && <p role="alert" className="mt-3 text-sm leading-relaxed text-red-700">{error}</p>}
      </footer>
    </dialog>
  );
}
